import { useRef, useSyncExternalStore } from 'react'

const MINUTE_MS = 60 * 1000

/**
 * ONE minute timer for every clock-derived label on screen, instead of one per component.
 *
 * Both of this app's big tables render these per row and virtualise, so the per-instance version cost a
 * `setInterval` for each visible row — 30-40 of them on a board, waking the main thread 30-40 times a minute
 * at scattered moments, each wake re-rendering its own row. The interval here is shared, started with the
 * first subscriber and cleared with the last, so one wake advances everything together: the same work React
 * had to do anyway, in a single pass.
 *
 * Exposed in two shapes because the two readers want different things, and both ride the one timer:
 *
 *   - {@link useMinuteTickCount} hands back an opaque counter. A component that reads the clock itself at
 *     render wants only "something changed"; it must NOT be given a stale shared timestamp, or a row
 *     scrolling into view mid-minute would report the age as of the last tick.
 *   - {@link useMinuteTick} hands back the instant the tick happened, frozen while disabled — the contract
 *     the detention/truck-motion chips were written against.
 */
let tick = 0
let timer: ReturnType<typeof setInterval> | undefined
const listeners = new Set<() => void>()

const subscribe = (notify: () => void): (() => void) => {
    listeners.add(notify)
    if (timer === undefined) {
        timer = setInterval(() => {
            tick += 1
            listeners.forEach((listener) => listener())
        }, MINUTE_MS)
    }
    return () => {
        listeners.delete(notify)
        if (listeners.size === 0 && timer !== undefined) {
            clearInterval(timer)
            timer = undefined
        }
    }
}

/** A subscription that never fires, for a disabled caller — so `enabled` costs no timer and no listener. */
const subscribeNever = (): (() => void) => () => {}

const getTick = (): number => tick
/** A disabled caller's snapshot has to be CONSTANT, not the live counter: `useSyncExternalStore` compares
 *  snapshots on every render, so handing back a number that other subscribers keep advancing would make a
 *  disabled component re-render — and, worse, look to the clock below like a tick it should read. */
const getNoTick = (): number => 0

/**
 * Re-renders the caller once a minute, off the one shared timer, and returns an opaque counter.
 *
 * The counter is deliberately not a timestamp: callers of this shape read `Date.now()` themselves while
 * rendering, which is what lets a row mounting halfway through a minute report its own age instead of
 * inheriting the shared timer's last reading.
 */
export const useMinuteTickCount = (enabled: boolean = true): number =>
    useSyncExternalStore(enabled ? subscribe : subscribeNever, enabled ? getTick : getNoTick, enabled ? getTick : getNoTick)

/**
 * The wall clock, re-read every minute while `enabled`, for labels derived from how old a timestamp is
 * (truck motion freshness, detention duration). Takes a reading on enable so a row that becomes eligible
 * without remounting — a load flipping to IN_TRANSIT over the socket — does not spend up to a minute
 * measuring against the clock as it was at mount.
 *
 * That catch-up read is conditional: rows mount already-enabled all the time (the table virtualises, so they
 * mount continuously while scrolling) and an unconditional `setNowMs` there would re-render every one of them
 * for a sub-millisecond difference that cannot change a minute-granularity label.
 *
 * Returns a `number` rather than a `Date` so it can be diffed without allocating.
 */
export const useMinuteTick = (enabled: boolean): number => {
    const ticks = useMinuteTickCount(enabled)
    // The last reading, with the inputs it was taken for.
    //
    // Read and written DURING RENDER, deliberately. The tick already re-renders every enabled caller, so the
    // clock has to be current in that same render; mirroring it into state from an effect instead commits
    // once with the old value and then schedules a second pass — two renders per minute for every enabled row
    // of a virtualized table, which is the opposite of what sharing the timer was for. Idempotent on a
    // repeated render (the second pass finds the tick already recorded and re-reads nothing), which is what
    // makes it safe under StrictMode's double invoke.
    const reading = useRef<{ ticks: number; nowMs: number; enabled: boolean } | null>(null)
    if (reading.current === null) reading.current = { ticks, nowMs: Date.now(), enabled }
    const last = reading.current

    if (enabled) {
        if (!last.enabled) {
            // Became eligible without remounting — a load flipping to IN_TRANSIT over the socket. Catch up
            // only across a real minute: rows mount and flip constantly, and a sub-minute re-read cannot
            // change a minute-granularity label, so it would cost a render for nothing.
            if (Date.now() - last.nowMs >= MINUTE_MS) last.nowMs = Date.now()
        } else if (ticks !== last.ticks) {
            last.nowMs = Date.now()
        }
        last.ticks = ticks
    }
    // Leaving `nowMs` where it was: a row that stops being eligible keeps the reading it had rather than
    // taking a fresh one on the way out.
    last.enabled = enabled

    return last.nowMs
}
